import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
  ingestCsv, getClient, downloadClientTemplate,
} from "../lib/api";
import { API } from "../lib/backend";
import { useAuth } from "../auth/AuthContext";
import { useWorkflowOptional } from "../workflow/WorkflowContext";
import { maskIossImNumber } from "../lib/mask";
import { DuplicateFileModal } from "./DuplicateFileModal";
import {
  XLSX_ADAPTERS, SINGLE_CURRENCY_ADAPTERS, CURRENCY_OPTIONS,
} from "./upload_panel/constants";
import {
  UploadSimple, FileCsv, Warning, Sparkle, ArrowSquareOut, DownloadSimple,
} from "@phosphor-icons/react";

export const UploadPanel = ({ onIngested }) => {
  const { t } = useTranslation();
  const { admin } = useAuth();
  const wf = useWorkflowOptional();
  const clientId = wf?.clientId || null;
  // Digital-Services scope: client_template is the only ingestion
  // source after the marketplace-adapter purge (A2-69 removed the chooser).
  const adapter = "client_template";
  const [declaredCurrency, setDeclaredCurrency] = useState("GBP");
  // v0.5.0i+19d — operator-declared default supply type at upload.
  // Empty string = "use adapter default (GOODS)". Applied as a
  // fallback for client_template rows whose per-row `Supply type`
  // cell is blank. Per-row values always win.
  const [defaultSupplyType, setDefaultSupplyType] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [lastReport, setLastReport] = useState(null);
  // v0.4.22.2 — duplicate-file confirm modal state.
  const [dupModal, setDupModal] = useState(null);
  // v0.4.37 CL1 — auto-detect reads from the SELECTED CLIENT PROFILE.
  const [autoDetect, setAutoDetect] = useState(null);
  const fileRef = useRef(null);


  useEffect(() => {
    // OPERATOR + ADMIN both read /api/clients. Empty state when no
    // client is selected so the banner never shows stale data.
    if (!clientId) {
      setAutoDetect({ noClient: true });
      return;
    }
    getClient(clientId)
      .then((c) => {
        const ossRegs = (c.company_vat_numbers || []).filter((e) => {
          const rt = (e.registration_type || "").toUpperCase();
          return rt === "OSS_UNION" || rt === "OSS_NON_UNION" || rt === "OSS";
        });
        const localRegs = (c.company_vat_numbers || []).filter((e) => {
          const rt = (e.registration_type || "LOCAL").toUpperCase();
          return rt === "LOCAL";
        });
        const imRaw = ((c.ioss_im_number || "")).trim().toUpperCase();
        const imValid = /^IM\d{10}$/.test(imRaw);
        const imDisplay = imValid ? maskIossImNumber(imRaw) : null;
        const sellerCountry = (localRegs[0]?.country
          || localRegs[0]?.country_code || "").toUpperCase();
        const sellerLabel = sellerCountry
          || (c.seller_establishment === "EU" ? "EU" : "NON-EU");
        setAutoDetect({
          adminOnly: false, noClient: false,
          seller: sellerLabel,
          iossMsi: (c.ioss_msi_country || "").toUpperCase() || null,
          iossImNumber: imValid ? imDisplay : null,
          scheme: imValid ? "seller" : "none",
          ossRegs: ossRegs.map((e) => ({
            country: (e.country || e.country_code || "").toUpperCase(),
            type: (e.registration_type || "").toUpperCase(),
          })),
          clientName: c.name, clientCode: c.code,
          iossImNumberRaw: imValid ? imRaw : null,
        });
        // v0.5.0i+21 Item 3 — sticky supply-type default. When the
        // client has a `default_supply_type` set on their profile,
        // pre-fill the upload dropdown so a SaaS-only or consultancy
        // client doesn't re-pick "Digital service" / "Professional
        // service" on every filing period. Operator can still
        // override at upload time. Only applies when the operator
        // hasn't already touched the dropdown this session.
        setDefaultSupplyType((c.default_supply_type || "").toUpperCase());
      })
      .catch(() => setAutoDetect({ error: true }));
  }, [clientId]);

  const showCurrency = SINGLE_CURRENCY_ADAPTERS.has(adapter);
  const showAutoDetectBanner = SINGLE_CURRENCY_ADAPTERS.has(adapter);

  const submit = async (file, { force = false } = {}) => {
    // v0.6.6 — neither this function nor the dropzone guarded against a
    // second submit firing while the first was still in flight: on a slow
    // ingest, clicking the dropzone again (or a second drop) could select
    // a different file and fire two overlapping ingestCsv calls, whose
    // resulting setLastReport/onIngested race and can show the report for
    // the wrong upload.
    if (busy) return;
    setBusy(true); setError(null);
    try {
      // v0.4.37 CL1 — IOSS / seller-country params from selected client profile.
      const opts = { force, client_id: clientId || undefined };
      const ad = autoDetect && !autoDetect.adminOnly && !autoDetect.noClient
        && !autoDetect.error ? autoDetect : null;
      if (showAutoDetectBanner && ad) {
        if (ad.scheme && ad.scheme !== "none") opts.ioss_scheme = ad.scheme;
        if (ad.scheme === "seller" && ad.iossImNumberRaw) {
          opts.ioss_number = ad.iossImNumberRaw;
        }
        if (ad.seller) opts.seller_country_override = ad.seller;
      }
      // v0.5.0i+19d — default supply type. Skipped when empty
      // (client_template rows then require a per-row value).
      if (defaultSupplyType) {
        opts.default_supply_type = defaultSupplyType;
      }
      const res = await ingestCsv(adapter, file,
        showCurrency ? declaredCurrency : undefined, opts);
      setLastReport(res);
      setDupModal(null);
      onIngested?.(res);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.error === "DUPLICATE_FILE") {
        setDupModal({ file, prior: detail.prior_run, message: detail.message });
      } else {
        setError(detail || e.message);
      }
    } finally { setBusy(false); }
  };

  return (
    <section data-testid="upload-panel" className="border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 rounded-xl">
      <div className="border-b border-slate-200 dark:border-slate-700 px-6 py-3 text-xs text-slate-500 dark:text-slate-400 bg-white dark:bg-slate-900 rounded-t-xl" data-testid="upload-section-subtitle">
        {t("ui.upload.section_subtitle")}
      </div>
      {/* A2-69 (2026-10-04) — the Source chooser is gone: client_template is the
          only ingestion source since the marketplace-adapter purge. */}
      <div className="bg-white dark:bg-slate-900 p-6 rounded-b-xl">
        {adapter === "client_template" && (
          <div
            data-testid="client-template-download-block"
            className="mb-4 rounded-lg border border-teal-200 dark:border-teal-800 bg-teal-50/60 dark:bg-teal-900/40 p-4"
          >
            <div className="text-sm font-semibold text-teal-800 dark:text-teal-200 mb-1">
              {t("ui.upload.client_template_download_title")}
            </div>
            <div className="text-xs text-slate-700 dark:text-slate-200 mb-3 leading-relaxed">
              {t("ui.upload.client_template_download_body")}
            </div>
            <div className="flex flex-wrap gap-2">
              {/* v0.6.12 — were plain <a href> anchors, so the browser made a
                  top-level GET carrying no Authorization header. That is why
                  the route had to be mounted with no auth at all — leaving an
                  unauthenticated endpoint that takes a caller-supplied
                  client_id and stamps the resolved CLIENT NAME onto the Guide
                  sheet. Now fetched as a blob on the authenticated client, the
                  same fix ClientHistory.jsx already made for the calc export.
                  The server still names the file via Content-Disposition. */}
              <button
                type="button"
                data-testid="client-template-download-xlsx"
                // The selected client is passed so the file is stamped
                // "Prepared for: <company>" on its Guide sheet (v0.6.10) —
                // without it the template is anonymous the moment it leaves
                // the app, which is what testing flagged.
                onClick={() => downloadClientTemplate("xlsx", clientId || null)}
                className="inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-2 rounded-md bg-teal-600 hover:bg-teal-700 text-white"
              >
                <DownloadSimple size={14} weight="bold" />
                {t("ui.upload.client_template_download_xlsx")}
              </button>
              <button
                type="button"
                data-testid="client-template-download-csv"
                onClick={() => downloadClientTemplate("csv")}
                className="inline-flex items-center gap-1.5 text-sm font-medium px-3 py-2 rounded-md border border-teal-300 dark:border-teal-700 text-teal-700 dark:text-teal-300 hover:bg-teal-100 dark:hover:bg-teal-900/60"
              >
                <DownloadSimple size={14} weight="bold" />
                {t("ui.upload.client_template_download_csv")}
              </button>
            </div>
          </div>
        )}
        {showAutoDetectBanner && (
          <AutoDetectBanner data={autoDetect} t={t} />
        )}
        {showCurrency && (
          <div className="mb-4">
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-2">
              {t("ui.upload.currency_label")}
            </label>
            <select
              data-testid="upload-declared-currency"
              value={declaredCurrency}
              onChange={(e) => setDeclaredCurrency(e.target.value)}
              className="bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 text-slate-900 dark:text-slate-50 px-3 py-2 tabular-nums text-sm rounded-lg w-full md:w-48 focus:border-teal-500 dark:focus:border-teal-400 focus:ring-2 focus:ring-teal-100 dark:focus:ring-teal-900 dark:ring-teal-900"
            >
              {CURRENCY_OPTIONS.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
        )}
        {/* A2-69 (2026-10-04) — the default supply type is a client setting
            (Entities › edit › section 7), not an upload-time control. It still rides
            the upload request so a file with a blank "Supply type" column
            is filled in; the page only says that it is in force. */}
        {defaultSupplyType && (
          <div
            data-testid="upload-default-supply-type-note"
            className="mb-4 text-xs text-slate-600 dark:text-slate-400 leading-relaxed"
          >
            {t("ui.upload.default_supply_type_from_profile", { label: t(`supply_types.${defaultSupplyType}`) })}
          </div>
        )}
        <label className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-2">{t("ui.upload.file_label")}</label>
        <div
          data-testid="upload-dropzone"
          className={`group border-2 border-dashed border-slate-300 dark:border-slate-700 hover:border-teal-400 dark:hover:border-teal-500 hover:bg-teal-50/40 bg-slate-50/60 dark:bg-slate-900/60 transition-all p-10 text-center rounded-xl ${
            busy ? "opacity-60 cursor-not-allowed pointer-events-none" : "cursor-pointer"
          }`}
          onClick={() => { if (!busy) fileRef.current?.click(); }}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            e.preventDefault();
            if (busy) return;
            const f = e.dataTransfer.files?.[0];
            if (f) submit(f);
          }}
        >
          <input
            ref={fileRef}
            data-testid="upload-csv-input"
            type="file"
            accept={
              XLSX_ADAPTERS.has(adapter)
                ? ".csv,.txt,.tsv,.xlsx,text/csv,text/plain,text/tab-separated-values,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                : ".csv,.txt,.tsv,text/csv,text/plain,text/tab-separated-values"
            }
            className="hidden"
            disabled={busy}
            onChange={(e) => { const f = e.target.files?.[0]; if (f) submit(f); }}
          />
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-teal-50 dark:bg-teal-900/40 border border-teal-200 dark:border-teal-800 group-hover:bg-teal-100 group-hover:border-teal-300 transition-colors">
            <UploadSimple size={28} className="text-teal-700 dark:text-teal-300" weight="bold" />
          </div>
          <div className="mt-4 text-base font-semibold text-slate-900 dark:text-slate-50">
            {busy
              ? t("ui.upload.reading")
              : XLSX_ADAPTERS.has(adapter)
                ? t("ui.upload.drop_hint_xlsx")
                : t("ui.upload.drop_hint")}
          </div>
          {!busy && (
            <div
              data-testid="upload-dropzone-supported-formats"
              className="mt-2 text-sm text-slate-500 dark:text-slate-400"
            >
              {t("ui.upload.supported_formats")}
            </div>
          )}
        </div>
      </div>

      {error && (
        <div data-testid="upload-error" className="border-t border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/40 px-4 py-3 text-red-700 dark:text-red-300 text-sm flex gap-2">
          <Warning size={16} weight="bold" /> {t("ui.upload.ingest_rejected")} · {error}
        </div>
      )}
      {lastReport && (
        <div data-testid="upload-result" className="border-t border-slate-200 dark:border-slate-700 grid grid-cols-2 md:grid-cols-5 gap-px bg-slate-100 dark:bg-slate-800">
          {[
            [t("ui.upload.stat_total"), lastReport.report.rows_total, "text-slate-900 dark:text-slate-50"],
            [t("ui.upload.stat_accepted"), lastReport.report.rows_accepted, "text-emerald-700 dark:text-emerald-300"],
            [t("ui.upload.stat_flagged"), lastReport.report.rows_flagged, "text-teal-700 dark:text-teal-300"],
            [t("ui.upload.stat_failed"), lastReport.report.rows_failed, "text-red-700 dark:text-red-300"],
            [t("ui.upload.stat_run"), lastReport.run.id.slice(0, 8), "text-slate-500 dark:text-slate-400"],
          ].map(([k, v, c]) => (
            <div key={k} className="bg-white dark:bg-slate-900 p-3">
              <div className="text-xs font-semibold text-slate-500 dark:text-slate-400">{k}</div>
              <div className={`tabular-nums text-xl ${c}`}>{v}</div>
            </div>
          ))}
          {lastReport.report.failures?.length > 0 && (
            <div className="col-span-full bg-red-50 dark:bg-red-900/40 border-t border-red-200 dark:border-red-800 p-3">
              <div className="text-xs tracking-normal text-red-700 dark:text-red-300 mb-2 flex items-center gap-2">
                <FileCsv size={14} /> {t("ui.upload.failed_rows_title")}
              </div>
              {lastReport.report.failures.slice(0, 10).map((f, i) => (
                <div key={i} className="text-xs text-red-700 dark:text-red-300">{/^Spreadsheet row /.test(f.reason || "") ? f.reason : `${t("ui.upload.row_label")} ${f.source_row} · ${f.reason}`}</div>
              ))}
            </div>
          )}
          {lastReport.report.notes?.length > 0 && (
            <div data-testid="upload-notes" className="col-span-full bg-amber-50 dark:bg-amber-900/30 border-t border-amber-200 dark:border-amber-800 p-3">
              {lastReport.report.notes.map((n, i) => (
                <div key={i} className="text-xs text-amber-800 dark:text-amber-300 flex items-start gap-2">
                  <Warning size={14} weight="bold" className="mt-0.5 shrink-0" /> {n}
                </div>
              ))}
            </div>
          )}
          {/* v0.6.8 — found live 2026-09-05: the same filename uploaded
              twice with a different IOSS scheme setting produced two
              runs that classified identically-named data completely
              differently (IOSS vs ordinary local sale), with nothing
              telling the operator this had happened. Non-blocking —
              could be entirely intentional (a genuine correction
              upload) — just makes it visible instead of silent. */}
          {lastReport.scheme_mismatch && (
            <div data-testid="upload-scheme-mismatch-warning" className="col-span-full bg-amber-50 dark:bg-amber-900/30 border-t border-amber-200 dark:border-amber-800 p-3">
              <div className="text-xs text-amber-800 dark:text-amber-300 flex items-start gap-2">
                <Warning size={14} weight="bold" className="mt-0.5 shrink-0" />
                {lastReport.scheme_mismatch.message}
              </div>
            </div>
          )}
        </div>
      )}
      {dupModal && (
        <DuplicateFileModal
          open
          prior={dupModal.prior}
          message={dupModal.message}
          thisFilename={dupModal.file?.name}
          thisAdapter={adapter}
          busy={busy}
          onCancel={() => setDupModal(null)}
          onForce={() => submit(dupModal.file, { force: true })}
        />
      )}
    </section>
  );
};

/**
 * Slim teal banner above the dropzone for single-currency adapters.
 * Surfaces seller country / IOSS scheme / OSS regs read by the
 * engine from the SELECTED CLIENT PROFILE at parse time. Missing
 * fields link to /admin/clients for editing.
 */
const AutoDetectBanner = ({ data, t }) => {
  if (!data) return null;
  if (data.noClient) {
    return (
      <div
        data-testid="upload-autodetect-banner"
        className="mb-3 flex items-center gap-2 text-[11px] rounded-xl border border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-950 px-3 py-2 text-slate-600 dark:text-slate-300"
      >
        <Sparkle size={14} weight="bold" className="text-teal-600 dark:text-teal-400 shrink-0" />
        <span data-testid="autodetect-no-client">{t("ui.upload.autodetect_no_client")}</span>
      </div>
    );
  }
  if (data.adminOnly) {
    return (
      <div
        data-testid="upload-autodetect-banner"
        className="mb-3 flex items-center gap-2 text-[11px] rounded-xl border border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-950 px-3 py-2 text-slate-600 dark:text-slate-300"
      >
        <Sparkle size={14} weight="bold" className="text-teal-600 dark:text-teal-400 shrink-0" />
        <span>{t("ui.upload.autodetect_admin_only")}</span>
      </div>
    );
  }
  if (data.error) {
    return (
      <div
        data-testid="upload-autodetect-banner"
        className="mb-3 flex items-center gap-2 text-[11px] rounded-xl border border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-950 px-3 py-2 text-slate-500 dark:text-slate-400"
      >
        <Warning size={14} className="text-amber-600 dark:text-amber-400 shrink-0" />
        <span>{t("ui.upload.autodetect_error")}</span>
      </div>
    );
  }
  return (
    <div
      data-testid="upload-autodetect-banner"
      className="mb-4 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs rounded-lg border border-teal-200 dark:border-teal-800 bg-teal-50 dark:bg-teal-900/40 px-3 py-2"
    >
      <div className="flex items-center gap-1.5 text-teal-800 dark:text-teal-200 font-semibold shrink-0">
        <Sparkle size={14} weight="bold" />
        {t("ui.upload.autodetect_title")}
      </div>
      <BannerField
        label={t("ui.upload.autodetect_seller")}
        value={data.seller}
        testid="autodetect-seller"
        configureLabel={t("ui.upload.autodetect_configure")}
      />
      <BannerField
        label={t("ui.upload.autodetect_ioss")}
        value={
          data.iossImNumber
            ? `${t("ui.upload.autodetect_ioss_seller_owned")} (${data.iossImNumber})`
            : null
        }
        testid="autodetect-ioss"
        configureLabel={t("ui.upload.autodetect_not_set")}
      />
      <BannerField
        label={t("ui.upload.autodetect_oss")}
        value={
          data.ossRegs.length
            ? data.ossRegs
                .map((r) => `${r.country} (${r.type === "OSS_UNION" ? "union" : "non-union"})`)
                .join(", ")
            : null
        }
        testid="autodetect-oss"
        configureLabel={t("ui.upload.autodetect_not_set")}
      />
    </div>
  );
};

const BannerField = ({ label, value, testid, configureLabel }) => (
  <div className="flex items-center gap-1.5" data-testid={testid}>
    <span className="text-slate-500 dark:text-slate-400 tracking-normal">{label}:</span>
    {value ? (
      <span className="text-slate-900 dark:text-slate-50">{value}</span>
    ) : (
      // v0.6.8 fix — was a plain <a href>; see RegistrationsWarning.jsx
      // for why that wipes the in-progress workflow session.
      <Link
        to="/admin/clients"
        data-testid={`${testid}-configure`}
        className="inline-flex items-center gap-1 text-amber-700 dark:text-amber-300 hover:text-amber-700 underline decoration-dotted"
      >
        {configureLabel}
        <ArrowSquareOut size={11} weight="bold" />
      </Link>
    )}
  </div>
);
